// Mock extraction for the Aug 11 demo.
//
// No Vision or STT runs anywhere in this build — but the item descriptions,
// units, FOB values and catalog UUIDs are pulled live from customs_catalog,
// the same reference table the operator's Anexo is priced from. If an exec
// cross-checks a line against the catalog, it matches.
//
// customs_catalog is anon-readable ("Public can read customs catalog", roles
// {anon, authenticated}), so this works with no cliente session.
//
// Deliberate imperfection: not every line gets a catalog_match_id. Real Vision
// extraction misses, and a demo where every row matches at 0.99 reads as fake.

import { supabase } from '../../supabaseClient';

export const VOICE_TRANSCRIPT = 'una caja con dos camisas, un shampoo y un cargador';

// Consumer-goods band. Keeps the demo away from $255 battery chargers.
const FOB_MIN = 3;
const FOB_MAX = 45;

// Used only when the catalog has no row for a term. "SHAMPOO" is the real
// case: the catalog has no entry under that spelling, so this line lands
// unmatched with lower confidence — which is exactly what we want to show.
const FALLBACK = {
  shampoo: {
    id: null,
    descripcion: 'SHAMPOO 500ML MARCA COMERCIAL',
    unidad_medida: 'UNIDAD',
    fob_usd: 8.0,
  },
};

let catalogPromise = null;

// One read per session, reused by every capture.
export const loadCatalog = () => {
  if (!catalogPromise) {
    catalogPromise = supabase
      .from('customs_catalog')
      .select('id, descripcion, unidad_medida, fob_usd, categoria')
      .gte('fob_usd', FOB_MIN)
      .lte('fob_usd', FOB_MAX)
      .limit(500)
      .then(({ data, error }) => {
        if (error) {
          console.error('[Desarrollos/intake] no se pudo leer customs_catalog', error);
          return [];
        }
        return data || [];
      });
  }
  return catalogPromise;
};

// 12–15 items spread across distinct categories, so image captures can mix
// categories and factura captures can stay inside one.
const diversePool = (catalog) => {
  const byCategory = new Map();
  catalog.forEach((row) => {
    const key = row.categoria || 'OTROS';
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(row);
  });
  return byCategory;
};

const findTerm = (catalog, term) =>
  catalog.find((r) => (r.descripcion || '').toUpperCase().includes(term)) || null;

// Confidence is cosmetic, but it should correlate with whether we matched.
const toItem = (row, cantidad, { matched = true } = {}) => ({
  descripcion: row.descripcion,
  cantidad,
  unidad_medida: row.unidad_medida || 'UNIDAD',
  fob_unitario_usd: Number(row.fob_usd || 0),
  catalog_match_id: matched ? row.id || null : null,
  confidence: matched && row.id ? 0.86 + ((cantidad * 3) % 11) / 100 : 0.58,
});

export const buildExtraction = async (sourceType) => {
  const catalog = await loadCatalog();

  // ── voice: must match the fixed transcript literally ────────────────────
  if (sourceType === 'voice') {
    const camisa = findTerm(catalog, 'CAMISA');
    const shampoo = findTerm(catalog, 'SHAMPOO');
    const cargador =
      findTerm(catalog, 'CARGADOR PARA TELEFONO') || findTerm(catalog, 'CARGADOR');

    return {
      mock: true,
      source_hint: 'voice',
      items: [
        camisa
          ? toItem(camisa, 2)
          : toItem({ ...FALLBACK.shampoo, descripcion: 'CAMISA DE OTRAS MARCAS', fob_usd: 3.59 }, 2, { matched: false }),
        shampoo ? toItem(shampoo, 1) : toItem(FALLBACK.shampoo, 1, { matched: false }),
        cargador
          ? toItem(cargador, 1)
          : toItem({ ...FALLBACK.shampoo, descripcion: 'CARGADOR PARA TELEFONO CELULAR', fob_usd: 4.48 }, 1, { matched: false }),
      ],
    };
  }

  const byCategory = diversePool(catalog);
  const categories = Array.from(byCategory.keys());

  // ── factura: 4–5 lines from ONE category, like a single-store receipt ───
  if (sourceType === 'factura') {
    const biggest = categories
      .map((c) => byCategory.get(c))
      .sort((a, b) => b.length - a.length)[0] || [];
    const rows = biggest.slice(0, 5);
    return {
      mock: true,
      source_hint: 'factura',
      items: rows.map((row, i) =>
        // Last line intentionally unmatched — invoices always have one odd item.
        toItem(row, (i % 3) + 1, { matched: i < rows.length - 1 })
      ),
    };
  }

  // ── image: 2–3 lines across mixed categories, like a small pile ─────────
  const picks = [];
  for (const cat of categories) {
    const rows = byCategory.get(cat);
    if (rows && rows.length) picks.push(rows[0]);
    if (picks.length === 3) break;
  }
  return {
    mock: true,
    source_hint: 'image',
    items: picks.map((row, i) => toItem(row, i === 0 ? 2 : 1, { matched: i !== picks.length - 1 })),
  };
};
